import type { BuildNodeData, PointData } from '../config/map'
import type { EnemyKind, EnemyModifier } from './enemies'

export type MapId = 'GENESIS_NETWORK' | 'NEON_EXCHANGE' | 'VALIDATOR_HEIGHTS'

export type MapHazard = 'NONE' | 'VOLATILITY_SURGE' | 'LIGHTNING_STORM'

export interface SectorWaveIdentity {
  readonly title: string
  readonly modifier: EnemyModifier
  readonly modifierLabel: string
  readonly modifierTargets: readonly EnemyKind[]
}

export interface SectorBossProfile {
  readonly name: string
  readonly role: string
  readonly modifier: EnemyModifier
  readonly hpMultiplier: number
  readonly speedMultiplier: number
  readonly rewardMultiplier: number
  readonly armorBonus: number
  readonly coreDamage: number
  readonly color: number
}

export interface MapDefinition {
  readonly id: MapId
  readonly name: string
  readonly sector: string
  readonly description: string
  readonly textureKey: string
  readonly texturePath: string
  readonly accent: number
  readonly accentCss: string
  readonly requiredLevel: number
  readonly paths: readonly (readonly PointData[])[]
  readonly buildNodes: readonly BuildNodeData[]
  readonly portals: readonly PointData[]
  readonly hazard: MapHazard
  readonly hazardLabel: string
  readonly balance: {
    readonly enemyHp: number
    readonly enemySpeed: number
    readonly reward: number
    readonly towerRange: number
    readonly spawnRate: number
  }
  readonly waveAdditions: readonly Partial<Record<EnemyKind, number>>[]
  readonly waveIdentities: readonly SectorWaveIdentity[]
  readonly boss: SectorBossProfile
  readonly masteryCoreThresholds: readonly [number, number]
  readonly masteryObjectives: readonly [string, string, string]
}

export const MAP_DEFINITIONS: Record<MapId, MapDefinition> = {
  GENESIS_NETWORK: {
    id: 'GENESIS_NETWORK',
    name: 'GENESIS NETWORK',
    sector: 'SECTOR 00 // ORIGIN',
    description: 'Balanced routing with six universal defense sockets.',
    textureKey: 'genesis-network-bg',
    texturePath: '/assets/genesis-network-bg.webp',
    accent: 0x35d8ea,
    accentCss: '#35d8ea',
    requiredLevel: 1,
    paths: [[
      { x: 1270, y: 88 }, { x: 1110, y: 198 }, { x: 945, y: 237 }, { x: 805, y: 327 }, { x: 682, y: 322 },
      { x: 578, y: 248 }, { x: 470, y: 301 }, { x: 350, y: 275 }, { x: 255, y: 328 }, { x: 145, y: 356 },
    ]],
    buildNodes: [
      { id: 'N-01', x: 1030, y: 342 }, { id: 'N-02', x: 874, y: 168 }, { id: 'N-03', x: 744, y: 414 },
      { id: 'N-04', x: 596, y: 168 }, { id: 'N-05', x: 340, y: 168 }, { id: 'N-06', x: 430, y: 440 },
    ],
    portals: [{ x: 1002, y: 231 }],
    hazard: 'NONE',
    hazardLabel: 'STABLE ROUTING',
    balance: { enemyHp: 1, enemySpeed: 1, reward: 1, towerRange: 1, spawnRate: 1 },
    waveAdditions: [{}, {}, {}, {}, {}],
    waveIdentities: [
      { title: 'ORIGIN PROBE', modifier: 'NONE', modifierLabel: 'STANDARD SIGNATURES', modifierTargets: [] },
      { title: 'PACKET FLOOD', modifier: 'OVERCLOCKED', modifierLabel: 'OVERCLOCKED BOTS', modifierTargets: ['BOT'] },
      { title: 'RUMOR ENGINE', modifier: 'REGENERATING', modifierLabel: 'SELF-REPAIRING FUD', modifierTargets: ['FUD_BOT'] },
      { title: 'CAPITAL SIEGE', modifier: 'HARDENED', modifierLabel: 'HARDENED LIQUIDATORS', modifierTargets: ['LIQUIDATOR'] },
      { title: 'GENESIS RECKONING', modifier: 'REGENERATING', modifierLabel: 'LEDGER TITAN INBOUND', modifierTargets: [] },
    ],
    boss: { name: 'LEDGER TITAN', role: 'Boss / self-repairing ledger', modifier: 'REGENERATING', hpMultiplier: 1, speedMultiplier: 1, rewardMultiplier: 1, armorBonus: 0, coreDamage: 50, color: 0xffb347 },
    masteryCoreThresholds: [60, 90],
    masteryObjectives: ['SECURE THE NETWORK', 'FINISH WITH 60% CORE', 'FINISH WITH 90% CORE'],
  },
  NEON_EXCHANGE: {
    id: 'NEON_EXCHANGE',
    name: 'NEON EXCHANGE',
    sector: 'SECTOR 01 // MARKET',
    description: 'Angular routing favors overlapping fields of fire.',
    textureKey: 'neon-exchange-bg',
    texturePath: '/assets/neon-exchange-bg.webp',
    accent: 0xff4fd8,
    accentCss: '#ff4fd8',
    requiredLevel: 2,
    paths: [[
      { x: 1270, y: 165 }, { x: 1110, y: 270 }, { x: 970, y: 282 }, { x: 865, y: 350 }, { x: 754, y: 363 },
      { x: 650, y: 310 }, { x: 540, y: 358 }, { x: 430, y: 288 }, { x: 315, y: 335 }, { x: 134, y: 302 },
    ]],
    buildNodes: [
      { id: 'X-01', x: 1043, y: 386 }, { id: 'X-02', x: 888, y: 184 }, { id: 'X-03', x: 778, y: 432 },
      { id: 'X-04', x: 681, y: 218 }, { id: 'X-05', x: 410, y: 176 }, { id: 'X-06', x: 469, y: 448 },
    ],
    portals: [{ x: 1035, y: 285 }],
    hazard: 'VOLATILITY_SURGE',
    hazardLabel: 'VOLATILITY SURGE',
    balance: { enemyHp: 1, enemySpeed: 1.08, reward: 1.2, towerRange: 1, spawnRate: 1.08 },
    waveAdditions: [{ PAPER_HAND: 2 }, { PAPER_HAND: 3 }, { PAPER_HAND: 3, FUD_BOT: 1 }, { PAPER_HAND: 4 }, { PAPER_HAND: 5, FUD_BOT: 1 }],
    waveIdentities: [
      { title: 'FLASH ORDERS', modifier: 'OVERCLOCKED', modifierLabel: 'OVERCLOCKED PAPER HANDS', modifierTargets: ['PAPER_HAND'] },
      { title: 'PANIC SELL', modifier: 'OVERCLOCKED', modifierLabel: 'ALL PAPER HANDS ACCELERATED', modifierTargets: ['PAPER_HAND'] },
      { title: 'SPOOFING ENGINE', modifier: 'REGENERATING', modifierLabel: 'SELF-REPAIRING FUD', modifierTargets: ['FUD_BOT'] },
      { title: 'LIQUIDATION CASCADE', modifier: 'OVERCLOCKED', modifierLabel: 'OVERCLOCKED LIQUIDATORS', modifierTargets: ['LIQUIDATOR'] },
      { title: 'MARKET BLACKOUT', modifier: 'OVERCLOCKED', modifierLabel: 'FLASH CRASH INBOUND', modifierTargets: ['PAPER_HAND'] },
    ],
    boss: { name: 'FLASH CRASH', role: 'Boss / hypervolatile execution', modifier: 'OVERCLOCKED', hpMultiplier: 0.86, speedMultiplier: 1.4, rewardMultiplier: 1.15, armorBonus: 0, coreDamage: 45, color: 0xff4fd8 },
    masteryCoreThresholds: [55, 85],
    masteryObjectives: ['SURVIVE ALL SURGES', 'FINISH WITH 55% CORE', 'FINISH WITH 85% CORE'],
  },
  VALIDATOR_HEIGHTS: {
    id: 'VALIDATOR_HEIGHTS',
    name: 'VALIDATOR HEIGHTS',
    sector: 'SECTOR 02 // CONSENSUS',
    description: 'Twin uplinks merge under a hostile lightning grid.',
    textureKey: 'validator-heights-bg',
    texturePath: '/assets/validator-heights-bg.webp',
    accent: 0x7be9ff,
    accentCss: '#7be9ff',
    requiredLevel: 3,
    paths: [
      [
        { x: 1275, y: 55 }, { x: 1110, y: 118 }, { x: 955, y: 170 }, { x: 830, y: 245 }, { x: 716, y: 321 },
        { x: 610, y: 304 }, { x: 505, y: 301 }, { x: 390, y: 301 }, { x: 260, y: 300 }, { x: 134, y: 298 },
      ],
      [
        { x: 1275, y: 588 }, { x: 1110, y: 520 }, { x: 960, y: 448 }, { x: 835, y: 392 }, { x: 716, y: 321 },
        { x: 610, y: 304 }, { x: 505, y: 301 }, { x: 390, y: 301 }, { x: 260, y: 300 }, { x: 134, y: 298 },
      ],
    ],
    buildNodes: [
      { id: 'V-01', x: 950, y: 138 }, { id: 'V-02', x: 570, y: 191 }, { id: 'V-03', x: 421, y: 298 },
      { id: 'V-04', x: 551, y: 440 }, { id: 'V-05', x: 957, y: 436 }, { id: 'V-06', x: 716, y: 321 },
    ],
    portals: [{ x: 1075, y: 132 }, { x: 1075, y: 505 }],
    hazard: 'LIGHTNING_STORM',
    hazardLabel: 'LIGHTNING GRID',
    balance: { enemyHp: 1.12, enemySpeed: 1, reward: 1.3, towerRange: 1.15, spawnRate: 1 },
    waveAdditions: [{ BOT: 2 }, { BOT: 2, PAPER_HAND: 2 }, { FUD_BOT: 1 }, { LIQUIDATOR: 2 }, { LIQUIDATOR: 2 }],
    waveIdentities: [
      { title: 'DUAL ATTESTATION', modifier: 'HARDENED', modifierLabel: 'HARDENED BOTS', modifierTargets: ['BOT'] },
      { title: 'FORK PRESSURE', modifier: 'OVERCLOCKED', modifierLabel: 'SPLIT-LANE ACCELERATION', modifierTargets: ['PAPER_HAND'] },
      { title: 'BYZANTINE SIGNAL', modifier: 'REGENERATING', modifierLabel: 'SELF-REPAIRING SUPPORT', modifierTargets: ['FUD_BOT'] },
      { title: 'SLASHING FRONT', modifier: 'HARDENED', modifierLabel: 'FORTIFIED LIQUIDATORS', modifierTargets: ['LIQUIDATOR'] },
      { title: 'FINALITY BREACH', modifier: 'HARDENED', modifierLabel: 'FINALITY BREAKER INBOUND', modifierTargets: ['LIQUIDATOR'] },
    ],
    boss: { name: 'FINALITY BREAKER', role: 'Boss / armored consensus breach', modifier: 'HARDENED', hpMultiplier: 1.3, speedMultiplier: 0.85, rewardMultiplier: 1.3, armorBonus: 0.18, coreDamage: 55, color: 0x7be9ff },
    masteryCoreThresholds: [50, 80],
    masteryObjectives: ['HOLD BOTH UPLINKS', 'FINISH WITH 50% CORE', 'FINISH WITH 80% CORE'],
  },
}

export const MAP_IDS = Object.keys(MAP_DEFINITIONS) as MapId[]

export function getMapDefinition(id: string): MapDefinition {
  return MAP_DEFINITIONS[id as MapId] ?? MAP_DEFINITIONS.GENESIS_NETWORK
}
